import { FieldType, ViewModelBase } from "@shoothill/core";
import { action, computed, observable, reaction, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import validator from "validator";

// View models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";

// Models
import { BackgroundInformationModel } from "Globals/Models/Domain/FormSections/BackgroundInformation/BackgroundInformationModel";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import { BackgroundInformationDTO } from "Globals/Models/DTO/BackgroundInformationDTO";

export default class BackgroundInformationViewModel extends FormSectionViewModelBase<BackgroundInformationModel> {
    constructor(backgroundInformationModel: BackgroundInformationModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, backgroundInformationModel);
        this.saveStore.addCallback(FormSectionsEnum.BackgroundInformation, this.save);
        this.saveFunction = this.save;
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.BackgroundInformation, this.canProceed);
        }
    }

    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isMyModelValid();
            let isMyModelDirty: boolean = this.isModelDirty;
            if (isMyModelValid && isMyModelDirty === true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                let upsertBackgroundInformation: BackgroundInformationDTO = this.model.toDto();
                const apiResult = await this.Post<BackgroundInformationDTO>(AppUrls.Server.ApplicationForm.Upsert.BackgroundInformation, upsertBackgroundInformation);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("BackgroundInformation:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.BackgroundInformation, this.canProceed);

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("BackgroundInformation:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };
    /**
     * Custom model validation function. Validates child view models.
     * @returns boolean If model is valid or not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) == false) {
            isValid = false;
        }
        return isValid;
    };

    /**
     * Clears conditional fields from the model.
     */
    private clearConditionalModelData(): void {
        if (StoresInstance.Domain.EthnicityStore.getRequiresOther(this.model.ethnicityId) == false) {
            this.model.setValue("ethnicityOther", "");
        }
        if (this.model.isBritishOrEUCitizen == true) {
            this.model.setValue("isPassportIndefinite", true);
            this.model.setValue("visaEndDate", null);
        }
        if (this.model.isPassportIndefinite == true) {
            this.model.setValue("visaEndDate", null);
        }
    }

    @action
    public handleInputChange = (): void => {
        this.clearConditionalModelData();
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
        this.updateSectionCompleted(FormSectionsEnum.BackgroundInformation, this.canProceed);
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<BackgroundInformationModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleFieldChange = (fieldName: keyof FieldType<BackgroundInformationModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleRadioChange = (fieldName: keyof FieldType<BackgroundInformationModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<BackgroundInformationModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.model.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.model.setValue(fieldName, val);
        }
        this.handleInputChange();
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            let field = _field as keyof FieldType<BackgroundInformationModel>;
            let value = this.model.getValue(field);
            if (field == "createdDate") return;
            if (field == "isDeleted") return;
            if (field == "disabilityOther") {
                console.log("disability other.");
                return;
            }
            if (field == "visaEndDate") {
                if (this.model.isPassportIndefinite == true) {
                    return;
                }
                if (this.model.isBritishOrEUCitizen == true) {
                    return;
                }
            }
            if (field == "ethnicityOther") {
                if (StoresInstance.Domain.EthnicityStore.getRequiresOther(this.model.ethnicityId) == false) {
                    return;
                }
            }
            if (value == "placeholder") {
                completed = false;
            }
            if (this.isEmptyNullOrUndefined(value) == true) {
                completed = false;
            }
            if (completed == false) {
                console.error("Field not completed: ", field, "Value: ", value);
            }
        });
        return completed;
    }

    public async isFieldValid(fieldName: keyof FieldType<BackgroundInformationModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    public async isValid(fieldName: keyof FieldType<BackgroundInformationModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (fieldName == "visaEndDate") {
            let date = value as Date;
            if ((date && !validator.isEmpty(date.toString())) || isRequired) {
                if (this.model.isPassportIndefinite == false && (date == null || validator.isEmpty(date.toString()))) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "ageId") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "gender") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "genderAtBirth") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "ethnicityId") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "ethnicityOther") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (StoresInstance.Domain.EthnicityStore.getRequiresOther(this.model.ethnicityId) == true && validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "sexualOrientationId") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "religionId") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "disabilityId") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "specialRequirementsId") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "nationalityId") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value) || value == "placeholder") {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (isValid == false) {
            console.log("Field Not valid: ", fieldName);
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<BackgroundInformationModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
